import { Controller, useForm } from "react-hook-form";
import { ClockLoader } from "react-spinners";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/button";
import Input from "../../components/input";
import SideImage from "../../assets/images/sideImage.png";
import AuthLayout from "../../components/layouts/auth-layout";
import { toast } from "sonner";
import useRequest from "../../components/hook/use-req";
import { PasswordLockIcon, ReloadIcon, SmallMailIcon } from "../../assets/icon";
import { useState } from "react";
import PasswordCriteria from "../../components/password-criteria";

// Public, unauthenticated screen — reached from the "Accept Invite" link in
// the org-invite email (org_invites.postman_collection.json). The email and
// the one-time code both ride in the link's own query string
// (?email=...&code=...), so the invitee never has to type or see the code;
// this page just reads them and submits the new password alongside them to
// POST /auth/accept-invite. This is a dedicated endpoint, separate from the
// forgot-password/reset-password flow.
const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const code = searchParams.get("code") || "";
  const navigate = useNavigate();
  const { handleSubmit, control, watch } = useForm();
  const { loading, makeRequest } = useRequest("/auth/accept-invite", "POST");
  const [viewPassword, setViewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordValue = watch("password");

  const handleAcceptInvite = handleSubmit(async (formData) => {
    const [response, status] = await makeRequest({
      email,
      code,
      new_password: formData.password,
      confirm_password: formData.confirm_password,
    });
    if (status >= 200 && status < 300 && response?.success) {
      toast.success(response.message || "Invite accepted. You can now log in.");
      navigate("/login");
    } else {
      toast.error(response?.detail?.message || response?.detail || response?.message || "This invite link is invalid or has expired.");
    }
  });

  const handleShowPassword = () => setViewPassword((prev) => !prev);
  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  // A tampered/incomplete link — nothing to submit against.
  if (!email || !code) {
    return (
      <AuthLayout sideImageSrc={SideImage}>
        <div className="w-full">
          <h6 className="text-[24px] font-medium text-[#3A3428] dark:text-gray-100">Invalid invite link</h6>
          <p className="text-[14px] text-[#434343] dark:text-gray-400 mt-2">
            This invite link is missing or incomplete. Ask whoever invited you to resend the invite, then open the link straight from that email.
          </p>
          <Link
            to="/login"
            className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors mt-5 inline-block"
          >
            Back to Sign In
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout sideImageSrc={SideImage}>
      <div className="mb-32">
        <h1 className="text-[32px] font-bold text-[#3A3428] dark:text-gray-100">
          Welcome to <span className="font-medium">ConvoHub</span>
        </h1>
        <p className="text-[16px] text-[#434343] dark:text-gray-400">Reach customers on every channel, effortlessly.</p>
      </div>
      <form onSubmit={handleAcceptInvite} className="w-full">
        <h6 className="text-[24px] font-medium text-[#3A3428] dark:text-gray-100">Accept Your Invite</h6>
        <span className="text-[14px] text-[#434343] dark:text-gray-400 mt-2 block">
          You've been invited to join a team on ConvoHub. Set a password to activate your account.
        </span>

        <Input label="Email" icon={<SmallMailIcon />} type="email" value={email} readOnly className="w-full mb-4 mt-7" />

        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            pattern: {
              value: /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
              message: "Password must be at least 8 characters long, include a number and a special character",
            },
          }}
          render={({ field, fieldState }) => (
            <div className="w-full relative">
              <Input
                label="Password"
                icon={<PasswordLockIcon />}
                type={viewPassword ? "text" : "password"}
                value={field.value}
                placeholder="Create a Password"
                className="w-full mb-4"
                onChange={field.onChange}
              />
              <button
                onClick={handleShowPassword}
                onMouseDown={handleMouseDownPassword}
                className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                type="button"
              >
                {!viewPassword ? "Show" : "Close"}
              </button>
              {passwordValue?.length > 0 && <PasswordCriteria password={passwordValue} />}
              {fieldState?.error && <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">{fieldState.error.message}</p>}
            </div>
          )}
        />

        <div className="mt-7">
          <Controller
            name="confirm_password"
            control={control}
            rules={{
              required: "Confirm password is required",
              validate: (value) => value === watch("password") || "Passwords do not match",
            }}
            render={({ field, fieldState }) => (
              <div className="w-full relative">
                <Input
                  label="Confirm Password"
                  icon={<ReloadIcon />}
                  type={showConfirmPassword ? "text" : "password"}
                  value={field.value}
                  placeholder="Re-enter Password"
                  className="w-full mb-4"
                  onChange={field.onChange}
                />
                <button
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                  type="button"
                >
                  {!showConfirmPassword ? "Show" : "Close"}
                </button>
                {fieldState?.error && <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">{fieldState.error.message}</p>}
              </div>
            )}
          />
        </div>

        <Button size="lg" variant="primary" className="w-full mt-5" type="submit">
          {loading ? <ClockLoader color="#ffffff" loading={loading} size={20} /> : "Activate Account"}
        </Button>

        <p className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400">
          Already have an account?{" "}
          <Link to="/login" className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors">
            Sign In
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default AcceptInvite;
